import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { Card } from '../../components/ui/Card'

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const nr = (n: number) => n.toLocaleString('ro-RO')
const d = (s: string) => new Date(s).toLocaleDateString('ro-RO')

interface FeesResp {
  rows: { docType: string; label: string; month: string; count: number; value: number }[]
  livrareNet: number
}
interface FeesDetailResp {
  category: string
  label: string
  total: number
  count: number
  truncated: boolean
  byType: { docType: string; label: string; count: number; value: number }[]
  lines: {
    docType: string
    label: string
    docId: string
    month: string
    orderId: string | null
    partNumber: string | null
    productName: string | null
    quantity: number | null
    value: number
  }[]
  danteTotalNet: number
  danteInvoices: {
    number: string
    category: string
    categoryName: string
    date: string
    isStorno: boolean
    totalNoVat: number
    totalVat: number
    totalWithVat: number
  }[]
}

// Categoriile de cost (desfasuratoare) -> codurile docType. `cost: true` = se aduna in Total.
const CATS = [
  { key: 'comision', label: 'Comisioane', codes: ['dc', 'dcs', 'dcdp'], cost: true },
  { key: 'fbe', label: 'Fulfillment FBE', codes: ['dfd', 'dfo', 'dfr', 'dhr'], cost: true },
  { key: 'ads', label: 'Ads (post-paid)', codes: ['dapo'], cost: true },
  { key: 'livrare', label: 'Comision Genius', codes: ['ded'], cost: true },
  { key: 'vouchere', label: 'Vouchere (credit)', codes: ['dv', 'dvs'], cost: false },
] as const

/**
 * Card "Costuri eMAG" reutilizabil (Dashboard + Incasari): comisionul efectiv afisat
 * separat de celelalte costuri (FBE, Ads, Livrare Genius, Vouchere), cu drill-down per
 * categorie (linii desfasurator + facturile Dante aferente) si export Excel.
 * salesValue (optional) -> arata "comision efectiv" ca % din vanzari.
 */
export function CostsCard({
  from,
  to,
  salesValue,
}: {
  from: string
  to: string
  salesValue?: number
}) {
  const [costDrill, setCostDrill] = useState<string | null>(null)
  const [showCost, setShowCost] = useState(false)
  const qs = () => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    const s = p.toString()
    return s ? `?${s}` : ''
  }
  const { data: fees } = useQuery<FeesResp>({
    queryKey: ['emag-fees-summary', from, to],
    queryFn: async () => (await api.get(`/emag/fees/summary${qs()}`)).data,
  })
  const { data: costDetail, isLoading: costDetailLoading } = useQuery<FeesDetailResp>({
    queryKey: ['emag-fees-detail', costDrill, from, to],
    queryFn: async () =>
      (await api.get(`/emag/fees/detail${qs()}${qs() ? '&' : '?'}category=${costDrill}`)).data,
    enabled: !!costDrill,
  })

  const valOf = (codes: readonly string[]) =>
    (fees?.rows ?? []).filter((r) => codes.includes(r.docType)).reduce((a, r) => a + r.value, 0)
  const vals = Object.fromEntries(CATS.map((c) => [c.key, valOf(c.codes)])) as Record<
    string,
    number
  >
  // Livrare Genius: desfasuratorul e 0 -> folosim costul din facturile FED (net).
  vals.livrare = fees?.livrareNet ?? 0
  const cTotal = CATS.filter((c) => c.cost).reduce((a, c) => a + vals[c.key], 0)
  const comisionEfectiv = salesValue && salesValue > 0 ? (vals.comision / salesValue) * 100 : null

  return (
    <Card
      title="Costuri eMAG (desfășurătoare)"
      actions={
        fees && fees.rows.length > 0 ? (
          <button
            onClick={() => void downloadXlsx(`/emag/fees/export${qs()}`, 'costuri-emag.xlsx')}
            className="rounded-md border border-b4l-border bg-white px-3 py-1.5 text-xs font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark"
          >
            Export Excel
          </button>
        ) : undefined
      }
    >
      {!fees || fees.rows.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">
          Niciun desfășurător de cost importat (dc = comision, dapo = ads).
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {CATS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setCostDrill((c) => (c === t.key ? null : t.key))}
                className={`rounded-lg border p-3 text-left transition hover:border-b4l-blue ${
                  costDrill === t.key ? 'border-b4l-blue bg-b4l-blue-light/20' : 'border-b4l-border'
                }`}
              >
                <div className="text-xs text-b4l-text-muted">
                  {t.label} {costDrill === t.key ? '▲' : '▾'}
                </div>
                <div
                  className={`text-lg font-semibold ${
                    t.cost ? 'text-amber-700' : 'text-b4l-green-dark'
                  }`}
                >
                  {lei(vals[t.key])} lei
                </div>
                {t.key === 'comision' && comisionEfectiv != null && (
                  <div className="text-[11px] text-b4l-text-muted">
                    efectiv {comisionEfectiv.toFixed(1)}% din vânzări
                  </div>
                )}
                {t.key === 'livrare' && (
                  <div className="text-[11px] text-b4l-text-muted">
                    contribuție seller (FED); livrarea o suportă eMAG
                  </div>
                )}
              </button>
            ))}
            <div className="rounded-lg border-2 border-amber-500 bg-amber-50 p-3">
              <div className="text-xs text-b4l-text-muted">Total costuri</div>
              <div className="text-lg font-semibold text-amber-800 sm:text-xl">
                {lei(cTotal)} lei
              </div>
              <div className="text-[11px] text-b4l-text-muted">comision + FBE + ads + Genius</div>
            </div>
          </div>

          {/* Drill-down per categorie de cost (click pe tile) */}
          {costDrill && (
            <div className="mt-3 rounded-lg border border-b4l-border bg-b4l-bg-soft/40 p-3">
              {costDetailLoading || !costDetail ? (
                <p className="text-xs text-b4l-text-muted">Se încarcă detaliul…</p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-semibold">{costDetail.label}</span>
                    <span className="text-b4l-text-muted">
                      {nr(costDetail.count)} linii · {lei(costDetail.total)} lei
                    </span>
                    {costDetail.byType.map((bt) => (
                      <span
                        key={bt.docType}
                        className="rounded border border-b4l-border bg-white px-2 py-0.5"
                      >
                        {bt.label}: {lei(bt.value)} ({nr(bt.count)})
                      </span>
                    ))}
                  </div>

                  {/* Facturi Dante echivalente (referinta) — suma globala lunara */}
                  {costDetail.danteInvoices.length > 0 && (
                    <div className="mb-3 rounded-md border border-b4l-border bg-white p-2">
                      <div className="mb-1 text-xs font-semibold text-b4l-text">
                        Facturi Dante (referință) ·{' '}
                        <span className="font-normal text-b4l-text-muted">
                          {nr(costDetail.danteInvoices.length)} facturi · net{' '}
                          {lei(costDetail.danteTotalNet)} lei
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                            <tr>
                              <th className="py-1 pr-3">Factură</th>
                              <th className="py-1 pr-3">Tip</th>
                              <th className="py-1 pr-3">Data</th>
                              <th className="py-1 pr-3 text-right">Net</th>
                              <th className="py-1 pr-3 text-right">TVA</th>
                              <th className="py-1 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costDetail.danteInvoices.map((inv) => (
                              <tr
                                key={inv.number}
                                className="border-b border-b4l-border last:border-0"
                              >
                                <td className="py-1 pr-3 font-mono">{inv.number}</td>
                                <td className="py-1 pr-3 text-b4l-text-muted">
                                  {inv.categoryName}
                                </td>
                                <td className="py-1 pr-3">{d(inv.date)}</td>
                                <td className="py-1 pr-3 text-right">{lei(inv.totalNoVat)}</td>
                                <td className="py-1 pr-3 text-right text-b4l-text-muted">
                                  {lei(inv.totalVat)}
                                </td>
                                <td className="py-1 text-right font-semibold">
                                  {lei(inv.totalWithVat)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-1 text-[11px] text-b4l-text-muted">
                        Referință — factura Dante e o sumă globală lunară, datată în altă lună decât
                        perioada costului; nu se compară 1:1 cu desfășurătorul.
                      </p>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                        <tr>
                          <th className="py-1.5 pr-3">Lună</th>
                          <th className="py-1.5 pr-3">Tip</th>
                          <th className="py-1.5 pr-3">Order ID</th>
                          <th className="py-1.5 pr-3">Produs</th>
                          <th className="py-1.5 text-right">Valoare</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costDetail.lines.map((l, i) => (
                          <tr key={i} className="border-b border-b4l-border last:border-0">
                            <td className="py-1.5 pr-3">{l.month}</td>
                            <td className="py-1.5 pr-3 text-b4l-text-muted">{l.label}</td>
                            <td className="py-1.5 pr-3 font-mono">{l.orderId ?? '—'}</td>
                            <td className="py-1.5 pr-3">{l.productName ?? l.partNumber ?? '—'}</td>
                            <td className="py-1.5 text-right font-semibold">{lei(l.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {costDetail.truncated && (
                    <p className="mt-2 text-[11px] text-b4l-text-muted">
                      Se afișează primele 1000 de linii (după valoare). Pentru toate, folosește
                      Export Excel.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="mt-2 flex justify-end">
            <button
              onClick={() => setShowCost((v) => !v)}
              className="text-xs font-semibold text-b4l-blue hover:text-b4l-blue-dark hover:underline"
            >
              {showCost ? 'Ascunde detaliu pe lună ▲' : 'Vezi detaliu pe lună / tip ▾'}
            </button>
          </div>
          {showCost && (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                  <tr>
                    <th className="py-1.5 pr-3">Lună</th>
                    <th className="py-1.5 pr-3">Tip</th>
                    <th className="py-1.5 pr-3 text-right">Linii</th>
                    <th className="py-1.5 text-right">Valoare (fără TVA)</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.rows.map((r, i) => (
                    <tr key={i} className="border-b border-b4l-border last:border-0">
                      <td className="py-1.5 pr-3">{r.month}</td>
                      <td className="py-1.5 pr-3">
                        {r.label} <span className="text-b4l-text-muted">({r.docType})</span>
                      </td>
                      <td className="py-1.5 pr-3 text-right">{nr(r.count)}</td>
                      <td className="py-1.5 text-right font-semibold text-amber-700">
                        {lei(r.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  )
}
